// ============================================================================
// DASHBOARD PAGE - With i18n Translation Support
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';
import {
  LayoutDashboard, FileText, TrendingUp, TrendingDown,
  Plus, Crown, Settings, LogOut, Menu, X, ChevronRight,
  Eye, Calendar, CreditCard, CheckCircle, ExternalLink,
  AlertCircle, Target
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import LanguageSelector from '../components/LanguageSelector';

// Get current month's start date
function getMonthStart() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();

  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [monthlyCount, setMonthlyCount] = useState(0);

  const MONTHLY_LIMIT = 3;
  const isRTL = i18n.dir() === 'rtl';

  // Check subscription status
  const isPro = profile?.subscription_status === 'active';

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data: historyData, error } = await supabase
          .from('assessments')
          .select('*')
          .eq('user_id', user.id)
          .order('completed_at', { ascending: false });

        if (error) {
          console.error('Error fetching assessments:', error);
        } else {
          setAssessmentHistory(historyData || []);
        }

        const monthStart = getMonthStart();
        const { count } = await supabase
          .from('assessments')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gte('completed_at', monthStart);

        setMonthlyCount(count || 0);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleViewResults = (assessment) => {
    if (!assessment) return;

    const resultsData = {
      compositeScore: assessment.composite_score,
      riskLevel: assessment.risk_level,
      timeHorizon: assessment.timeline,
      industryScore: assessment.industry_score,
      riskScore: assessment.risk_score,
      knowledgeScore: assessment.knowledge_score,
      readinessScore: assessment.readiness_score,
      keyRiskFactors: assessment.key_risk_factors || [],
      protectiveFactors: assessment.protective_factors || [],
      recommendations: assessment.recommendations || [],
      userType: assessment.user_type
    };

    localStorage.setItem('lab2040_assessment_results', JSON.stringify(resultsData));
    localStorage.setItem('lab2040_view_assessment_id', assessment.id);

    if (isPro) {
      navigate('/results/full');
    } else {
      navigate('/results');
    }
  };

  const handleNewAssessment = () => {
    if (monthlyCount >= MONTHLY_LIMIT) {
      alert(t('dashboard.limitReached', { limit: MONTHLY_LIMIT }));
      return;
    }
    navigate('/assessment');
  };

  const getRiskColor = (level) => {
    const colors = {
      CRITICAL: 'text-red-600 bg-red-50 border-red-200',
      HIGH: 'text-orange-600 bg-orange-50 border-orange-200',
      MODERATE: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      LOW: 'text-green-600 bg-green-50 border-green-200',
      MINIMAL: 'text-teal-600 bg-teal-50 border-teal-200'
    };
    return colors[level] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getRiskLabel = (level) => {
    const labels = {
      CRITICAL: t('riskLevels.critical'),
      HIGH: t('riskLevels.high'),
      MODERATE: t('riskLevels.moderate'),
      LOW: t('riskLevels.low'),
      MINIMAL: t('riskLevels.minimal')
    };
    return labels[level] || level;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const locale = i18n.language === 'ar' ? 'ar-SA' :
      i18n.language === 'de' ? 'de-DE' :
        i18n.language === 'fr' ? 'fr-FR' :
          i18n.language === 'es' ? 'es-ES' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const latestAssessment = assessmentHistory[0];

  const chartData = [...assessmentHistory].reverse().slice(-10).map(a => ({
    date: new Date(a.completed_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }),
    score: a.composite_score
  }));

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="font-serif text-2xl font-bold">
              <span className="text-gray-900">Lab</span>
              <span className="text-teal-500">2040</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="flex items-center gap-2 text-teal-600 font-medium">
                <LayoutDashboard className="w-4 h-4" />
                {t('nav.dashboard')}
              </Link>
              <button
                onClick={handleNewAssessment}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <FileText className="w-4 h-4" />
                {t('nav.assessment')}
              </button>
              <Link to="/settings" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <Settings className="w-4 h-4" />
                {t('nav.settings')}
              </Link>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              {/* Language Selector */}
              <LanguageSelector compact />

              {/* Membership Badge */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${isPro
                ? 'bg-gradient-to-r from-amber-100 to-teal-100 text-teal-700'
                : 'bg-gray-100 text-gray-600'
                }`}>
                {isPro && <Crown className="w-4 h-4" />}
                {isPro ? t('subscription.proMember') : t('subscription.free')}
              </div>

              {/* Profile */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-medium">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-gray-500 hover:text-gray-700"
                  title={t('nav.logout')}
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-3">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 text-teal-600 font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <LayoutDashboard className="w-4 h-4" />
                {t('nav.dashboard')}
              </Link>
              <button
                onClick={() => { handleNewAssessment(); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-gray-600 py-2 w-full"
              >
                <FileText className="w-4 h-4" />
                {t('nav.assessment')}
              </button>
              <Link
                to="/settings"
                className="flex items-center gap-2 text-gray-600 py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Settings className="w-4 h-4" />
                {t('nav.settings')}
              </Link>
              <div className="pt-3 border-t border-gray-200">
                <LanguageSelector />
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 text-red-600 py-2 w-full"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout')}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {/* Welcome Section */}
            <div className="mb-8">
              <h1 className="font-serif text-3xl font-bold text-gray-900">{t('dashboard.welcome')}</h1>
              <p className="text-gray-600 mt-1">{t('dashboard.subtitle')}</p>
            </div>

            {/* Pro Membership Card */}
            {isPro && (
              <div className="bg-gradient-to-r from-teal-500 via-teal-700 to-gray-500 rounded-2xl p-6 text-white mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                      <Crown className="w-7 h-7" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">{t('subscription.proMember')}</h2>
                      <p className="text-white/80 text-sm">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        {t('subscription.memberSince', { date: formatDate(profile?.subscription_start) })}
                      </p>
                    </div>
                  </div>
                  <a
                    href="https://billing.stripe.com/p/login/4gM7sMcdU6dN1UP0YE4Rq00"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t('subscription.manageBilling')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/20">
                  <div>
                    <p className="text-white/70 text-xs uppercase">{t('subscription.status')}</p>
                    <p className="font-semibold flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> {t('subscription.active')}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs uppercase">{t('subscription.plan')}</p>
                    <p className="font-semibold">{t('subscription.proMonthly')}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs uppercase">{t('subscription.fullReports')}</p>
                    <p className="font-semibold">{t('subscription.unlimited')}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs uppercase">{t('subscription.actionPlans')}</p>
                    <p className="font-semibold">{t('subscription.ninetyDayPlans')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Upgrade Card for Free Users */}
            {!isPro && (
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold">{t('subscription.upgradeToPro')}</h2>
                    <p className="text-white/80 mt-1">Get full reports, action plans, and unlimited assessments</p>
                  </div>
                  <a
                    href="https://buy.stripe.com/5kQeVecdU6dN9nh4aQ4Rq01"
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
                  >
                    <Crown className="w-5 h-5" />
                    {t('subscription.upgrade')}
                  </a>
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Latest Score */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">{t('dashboard.latestScore')}</span>
                  {latestAssessment && (
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${latestAssessment.risk_level === 'CRITICAL' || latestAssessment.risk_level === 'HIGH'
                      ? 'bg-red-100 text-red-700'
                      : latestAssessment.risk_level === 'MODERATE'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                      }`}>
                      {getRiskLabel(latestAssessment.risk_level)}
                    </span>
                  )}
                </div>
                <div className="font-serif text-4xl font-bold text-gray-900">
                  {latestAssessment?.composite_score || '--'}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {formatDate(latestAssessment?.completed_at)}
                </p>
              </div>

              {/* Total Assessments */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">{t('dashboard.totalAssessments')}</span>
                  <FileText className="w-5 h-5 text-gray-400" />
                </div>
                <div className="font-serif text-4xl font-bold text-gray-900">
                  {assessmentHistory.length}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {t('dashboard.allTime')}
                </p>
              </div>

              {/* Monthly Usage */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-500">{t('dashboard.thisMonth')}</span>
                  <Calendar className="w-5 h-5 text-gray-400" />
                </div>
                <div className="font-serif text-4xl font-bold text-gray-900">
                  {monthlyCount} / {MONTHLY_LIMIT}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {t('dashboard.assessmentsUsed')}
                </p>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-teal-600 to-cyan-600 rounded-2xl shadow-sm p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-teal-200">{t('dashboard.quickActions')}</span>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={() => handleViewResults(latestAssessment)}
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      {t('dashboard.viewResults')}
                    </span>
                    <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                  <Link
                    to="/action-plan"
                    className="w-full flex items-center justify-between px-4 py-2 bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      {t('nav.actionPlan')}
                    </span>
                    <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </Link>
                  <button
                    onClick={handleNewAssessment}
                    disabled={monthlyCount >= MONTHLY_LIMIT}
                    className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-colors ${monthlyCount >= MONTHLY_LIMIT
                      ? 'bg-white/5 text-white/50 cursor-not-allowed'
                      : 'bg-white text-teal-600 font-semibold hover:bg-teal-50'
                      }`}
                  >
                    <span className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      {t('dashboard.newAssessment')}
                    </span>
                    <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            {latestAssessment && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="font-serif text-xl font-bold text-gray-900 mb-6">{t('scores.scoreBreakdown')}</h2>
                <div className="space-y-6">
                  {/* Industry Risk */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{t('scores.industryRisk')}</span>
                      <span className="font-semibold text-gray-900">{latestAssessment.industry_score || 0}/100</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${(latestAssessment.industry_score || 0) > 60 ? 'bg-red-500' :
                          (latestAssessment.industry_score || 0) > 30 ? 'bg-amber-500' : 'bg-green-500'
                          }`}
                        style={{ width: `${latestAssessment.industry_score || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t('scores.industryRiskDesc')}</p>
                  </div>

                  {/* Task Exposure */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{t('scores.taskExposure')}</span>
                      <span className="font-semibold text-gray-900">{latestAssessment.risk_score || 0}/100</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${(latestAssessment.risk_score || 0) > 60 ? 'bg-red-500' :
                          (latestAssessment.risk_score || 0) > 30 ? 'bg-amber-500' : 'bg-green-500'
                          }`}
                        style={{ width: `${latestAssessment.risk_score || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t('scores.taskExposureDesc')}</p>
                  </div>

                  {/* AI Knowledge */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{t('scores.aiKnowledge')}</span>
                      <span className="font-semibold text-gray-900">{latestAssessment.knowledge_score || 0}/100</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${(latestAssessment.knowledge_score || 0) > 60 ? 'bg-green-500' :
                          (latestAssessment.knowledge_score || 0) > 30 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                        style={{ width: `${latestAssessment.knowledge_score || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t('scores.aiKnowledgeDesc')}</p>
                  </div>

                  {/* Readiness Score */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{t('scores.readinessScore')}</span>
                      <span className="font-semibold text-gray-900">{latestAssessment.readiness_score || 0}/100</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${(latestAssessment.readiness_score || 0) > 60 ? 'bg-green-500' :
                          (latestAssessment.readiness_score || 0) > 30 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                        style={{ width: `${latestAssessment.readiness_score || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t('scores.readinessScoreDesc')}</p>
                  </div>
                </div>

                {/* Risk/Protective Factors */}
                <div className="grid md:grid-cols-2 gap-4 mt-8">
                  <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                    <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> {t('scores.keyRiskFactors')}
                    </h3>
                    <ul className="space-y-2">
                      {(latestAssessment.key_risk_factors || ['High task automation potential', 'Industry facing AI disruption']).slice(0, 3).map((factor, i) => (
                        <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                          <span className="text-red-400 mt-1">•</span>
                          {factor}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                    <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> {t('scores.protectiveFactors')}
                    </h3>
                    <ul className="space-y-2">
                      {(latestAssessment.protective_factors || ['Adaptability and learning mindset', 'Unique human skills']).slice(0, 3).map((factor, i) => (
                        <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                          <span className="text-green-400 mt-1">•</span>
                          {factor}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Progress Chart */}
            {chartData.length > 1 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="font-serif text-xl font-bold text-gray-900 mb-6">{t('dashboard.progressOverTime')}</h2>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                      <Line type="monotone" dataKey="score" stroke="#6366f1" strokeWidth={3} dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-sm text-gray-500 text-center mt-4">{t('dashboard.lowerScoresBetter')}</p>
              </div>
            )}

            {/* Assessment History */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-serif text-xl font-bold text-gray-900">{t('dashboard.assessmentHistory')}</h2>
                <span className="text-sm text-gray-500">{assessmentHistory.length} total</span>
              </div>
              <div className="divide-y divide-gray-200">
                {assessmentHistory.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('dashboard.noAssessments')}</p>
                    <button
                      onClick={handleNewAssessment}
                      className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      {t('dashboard.takeFirst')}
                    </button>
                  </div>
                ) : (
                  assessmentHistory.map((assessment, index) => (
                    <button
                      key={assessment.id}
                      onClick={() => handleViewResults(assessment)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${getRiskColor(assessment.risk_level)}`}>
                          <span className="font-bold">{assessment.composite_score}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {getRiskLabel(assessment.risk_level)} {t('riskLevels.risk')}
                            {index === 0 && <span className="ms-2 text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{t('dashboard.latest')}</span>}
                          </p>
                          <p className="text-sm text-gray-500">{formatDate(assessment.completed_at)}</p>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
